import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Layout, Menu, Spin, message } from 'antd';
import { GlobalOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import UserDropdown from '../UserDropdown';
import useUserInfo from '../../hooks/useUserInfo';
import {
  ADMIN_PATH_PREFIX,
  UNREAD_BADGE_KEY,
  getFlatUserMenuItems,
  getUserMenuGroups,
} from '../../config/userMenu';
import { getUnreadMessageCount } from '../../server/api';
import { readToken } from '../../utils/userInfo';
import AdminShellContext from './context';
import s from './index.module.css';

const { Content, Header, Sider } = Layout;

// 管理中心内允许跳回前台的路径（首页、登录注册等），其余前台路径自动补 /admin 前缀
const FRONT_ESCAPE_PATHS = ['/', '/login', '/register', '/reset-password'];

// 单个路径重写：保证管理中心内的二级跳转（详情页、考试页等）不会丢掉管理中心外壳
const rewritePath = (path) => {
  if (typeof path !== 'string') return path;
  if (!path.startsWith('/') || path.startsWith('//')) return path;

  const purePath = path.split(/[?#]/)[0];
  if (purePath === ADMIN_PATH_PREFIX || purePath.indexOf(`${ADMIN_PATH_PREFIX}/`) === 0) return path;
  if (FRONT_ESCAPE_PATHS.includes(purePath)) return path;

  return `${ADMIN_PATH_PREFIX}${path}`;
};

// history.push / replace 支持字符串与 location 对象两种入参
const rewriteLocationDescriptor = (to) => {
  if (typeof to === 'string') return rewritePath(to);
  if (to && typeof to === 'object' && typeof to.pathname === 'string') {
    return { ...to, pathname: rewritePath(to.pathname) };
  }
  return to;
};

/**
 * 管理中心布局
 * 左侧：登录后功能菜单（与前台右上角下拉菜单同源）
 * 右侧：菜单对应内容
 * 顶部：折叠按钮 + 查看网站（切回前台）+ 用户信息
 */
const AdminLayout = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const { userInfo } = useUserInfo();
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const flatMenuItems = useMemo(() => getFlatUserMenuItems(userInfo), [userInfo]);

  // 管理中心挂载期间重写路由跳转目标，卸载后恢复，不影响前台行为
  useEffect(() => {
    const originalPush = history.push;
    const originalReplace = history.replace;

    history.push = (to, state) => originalPush(rewriteLocationDescriptor(to), state);
    history.replace = (to, state) => originalReplace(rewriteLocationDescriptor(to), state);

    return () => {
      history.push = originalPush;
      history.replace = originalReplace;
    };
  }, [history]);

  useEffect(() => {
    if (!userInfo?.id) {
      setUnreadCount(0);
      return undefined;
    }

    let cancelled = false;

    const loadUnreadCount = async () => {
      try {
        const res = await getUnreadMessageCount({ userId: userInfo.id });
        const count = Number(res.data?.result ?? 0);
        if (!cancelled) {
          setUnreadCount(Number.isNaN(count) ? 0 : count);
        }
      } catch (_) {
        if (!cancelled) {
          setUnreadCount(0);
        }
      }
    };

    loadUnreadCount();

    return () => {
      cancelled = true;
    };
  }, [userInfo?.id, location.pathname]);

  const menuItems = useMemo(
    () =>
      getUserMenuGroups(userInfo).map((group) => ({
        type: 'group',
        key: group.key,
        label: group.title,
        children: group.items.map((item) => ({
          key: item.key,
          icon: item.icon,
          label:
            item.badgeKey === UNREAD_BADGE_KEY && unreadCount > 0 ? (
              <span className={s.menuLabel}>
                <span>{item.label}</span>
                <Badge count={unreadCount} size="small" />
              </span>
            ) : (
              item.label
            ),
        })),
      })),
    [userInfo, unreadCount]
  );

  const relativePath = location.pathname.slice(ADMIN_PATH_PREFIX.length) || '/';

  const selectedKeys = useMemo(() => {
    const matched = flatMenuItems.find(
      (item) => item.path && (relativePath === item.path || relativePath.indexOf(`${item.path}/`) === 0)
    );
    return matched ? [matched.key] : [];
  }, [flatMenuItems, relativePath]);

  const handleMenuClick = ({ key }) => {
    const target = flatMenuItems.find((item) => item.key === key);
    if (!target) return;

    if (target.placeholder) {
      message.warning(`「${target.label}」功能开发中`);
      return;
    }

    if (target.path) {
      history.push(`${ADMIN_PATH_PREFIX}${target.path}`);
    }
  };

  // 未登录：回到前台首页（前台右上角登录后会自动进入管理中心）
  if (!readToken()) {
    return <Redirect to="/" />;
  }

  if (!userInfo) {
    return (
      <div className={s.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout className={s.adminLayout}>
      <Sider
        className={s.sider}
        theme="light"
        width={220}
        collapsible
        collapsed={collapsed}
        trigger={null}
      >
        <div className={s.brand}>
          <img src="/logo.jpg" alt="logo" className={s.brandLogo} />
          {!collapsed && <span className={s.brandText}>管理中心</span>}
        </div>
        <div className={s.menuWrap}>
          <Menu
            mode="inline"
            items={menuItems}
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
          />
        </div>
      </Sider>

      <Layout>
        <Header className={s.header}>
          <Button
            type="text"
            aria-label={collapsed ? '展开菜单' : '收起菜单'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((prev) => !prev)}
          />
          <div className={s.headerRight}>
            <UserDropdown adminMode />
          </div>
        </Header>

        <Content className={s.content}>
          {/* 打上管理中心标记：内部页面据此隐藏自带的页头 logo 与页脚 */}
          <div className={s.contentInner}>
            <AdminShellContext.Provider value>{children}</AdminShellContext.Provider>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
